"use client";

import { useState } from "react";
import { Droplet, Shield, Zap, Waves, PaintBucket, Building2, Thermometer, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const [activeFilter, setActiveFilter] = useState("WSZYSTKIE");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const filters = ["WSZYSTKIE", "DACHY", "TARASY", "PRZEMYSŁ", "RENOWACJA"];
  const services = [{
    index: "§ 01.1",
    title: "Hydroizolacja dachów płaskich",
    description: "Bezspoinowe membrany natryskowe tworzące jednolitą, wodoszczelną powłokę. Idealne rozwiązanie dla dachów z papy, eliminujące ryzyko przecieków na lata.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "HYD",
    category: "DACHY",
    Icon: Droplet
  }, {
    index: "§ 01.2",
    title: "Renowacja dachów skośnych",
    description: "Profesjonalne uszczelnianie i malowanie dachów skośnych. Przywracamy estetykę oraz pełną szczelność pokrycia bez konieczności kosztownej wymiany.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "REN",
    category: "DACHY",
    Icon: PaintBucket
  }, {
    index: "§ 01.3",
    title: "Hydroizolacja tarasów i balkonów",
    description: "Systemy żywiczne odporne na zmienne warunki atmosferyczne i promieniowanie UV. Trwała ochrona przed wilgocią dla Twojego tarasu.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "TAR",
    category: "TARASY",
    Icon: Waves
  }, {
    index: "§ 01.4",
    title: "Dachy hal i obiektów przemysłowych",
    description: "Szybka i skuteczna hydroizolacja wielkopowierzchniowa. Technologia natryskowa pozwala na pracę bez przerywania ciągłości produkcji w zakładzie.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "IND",
    category: "PRZEMYSŁ",
    Icon: Building2
  }, {
    index: "§ 01.5",
    title: "Naprawa przecieków dachowych",
    description: "Szybka lokalizacja i eliminacja nieszczelności. Stosujemy zaawansowane membrany, które trwale uszczelniają problematyczne miejsca.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "FIX",
    category: "RENOWACJA",
    Icon: Wrench
  }, {
    index: "§ 01.6",
    title: "Izolacja termiczna i UV",
    description: "Nasze powłoki nie tylko chronią przed wodą, ale również odbijają promieniowanie UV, obniżając temperaturę wewnątrz budynku latem.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "UV",
    category: "RENOWACJA",
    Icon: Thermometer
  }, {
    index: "§ 01.7",
    title: "Ochrona posadzek przemysłowych",
    description: "Wzmocnienie i uszczelnienie posadzek betonowych w magazynach i halach. Odporność na ścieranie, chemikalia i intensywną eksploatację.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "FLO",
    category: "PRZEMYSŁ",
    Icon: Shield
  }, {
    index: "§ 01.8",
    title: "Technologia natryskowa",
    description: "Nowoczesna metoda bezspoinowa. Gwarancja szczelności, elastyczności i odporności na ekstremalne warunki pogodowe w całym regionie.",
    price: "WYCENA INDYWIDUALNA",
    coords: "HR-TECH 2024",
    stamp: "TEC",
    category: "DACHY",
    Icon: Zap
  }];
  const visibleServices = activeFilter === "WSZYSTKIE" ? services : services.filter(s => s.category === activeFilter);
  return <section data-section-id="1414" id="nasze-specjalizacje" className="relative bg-background py-32 md:py-44 overflow-hidden">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div ref={headerRef} className={`lg:sticky lg:top-[120px] relative transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <div aria-hidden="true" className="pointer-events-none absolute -top-12 -left-4 font-serif font-extralight leading-none select-none" style={{
              fontSize: "16vw",
              color: "var(--foreground)",
              opacity: 0.07,
              WebkitTextStroke: "1px var(--foreground)",
              WebkitTextFillColor: "transparent"
            }}>
                HR
              </div>

              <div className="relative">
                <div className="font-mono text-xs md:text-sm tracking-[0.18em] uppercase text-muted-foreground mb-8">
                  § 01 &mdash; NASZE ROZWIĄZANIA
                </div>

                <h2 className="font-default font-normal text-foreground" style={{
                fontSize: "clamp(2.25rem, 4.5vw, 4.5rem)",
                lineHeight: 1.02,
                letterSpacing: "-0.02em"
              }}>
                  Profesjonalna{" "}
                  <em className="font-normal italic" style={{
                  color: "var(--ring)"
                }}>
                    hydroizolacja
                  </em>
                  .
                </h2>

                <div className="flex items-center gap-3 mt-10 mb-8">
                  <div className="h-px flex-1 bg-foreground" />
                  <div className="size-1.5 rounded-lg" style={{
                  backgroundColor: "var(--ring)"
                }} />
                </div>

                <p className="font-default font-normal text-foreground/90" style={{
                fontSize: "clamp(1.125rem, 1.4vw, 1.375rem)",
                lineHeight: 1.55,
                letterSpacing: "-0.005em"
              }}>
                  HydroRoof to zaawansowane technologie uszczelniania dachów i tarasów. Zapewniamy trwałość, odporność na UV i pełną ochronę przed wodą.
                </p>

                <div className="flex flex-wrap gap-2.5 mt-10">
                  {filters.map((filter, i) => {
                  const isActive = activeFilter === filter;
                  return <Button key={filter} onClick={() => setActiveFilter(filter)} variant={isActive ? "default" : "outline"} className="font-mono text-[0.7rem] tracking-[0.12em] uppercase px-3 py-1 h-auto border border-foreground rounded-lg transition-all duration-300" style={{
                    backgroundColor: isActive ? "var(--ring)" : "transparent",
                    color: isActive ? "var(--primary-foreground)" : "var(--foreground)",
                    borderColor: isActive ? "var(--ring)" : "var(--foreground)"
                  }}>
                        {filter}
                      </Button>;
                })}
                </div>

                <div className="font-mono text-xs tracking-[0.08em] text-muted-foreground mt-10">
                  Śląsk / Małopolska / Opolskie
                </div>
              </div>
            </div>
          </div>

          <div ref={gridRef} className="lg:col-span-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {visibleServices.map((service, i) => {
              const ServiceIcon = service.Icon;
              return <div key={service.index} className={`group relative bg-card border border-foreground p-7 md:p-8 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-[1.5px] ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                transitionDelay: gridVisible ? `${i * 80}ms` : "0ms",
                borderRadius: "2px"
              }} onMouseEnter={e => {
                e.currentTarget.style.borderColor = "var(--ring)";
              }} onMouseLeave={e => {
                e.currentTarget.style.borderColor = "var(--foreground)";
              }}>
                    <div className="flex items-start justify-between mb-8">
                      <div className="font-mono text-xs tracking-[0.12em] uppercase text-muted-foreground">
                        {service.index}
                      </div>
                      <ServiceIcon className="size-12 text-foreground" strokeWidth={1.25} aria-hidden="true" />
                    </div>

                    <h3 className="font-default font-medium text-foreground mb-4" style={{
                  fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
                  lineHeight: 1.2,
                  letterSpacing: "-0.01em"
                }}>
                      {service.title}
                    </h3>

                    <p className="font-sans text-[0.95rem] text-foreground/80 leading-[1.6] mb-10">
                      {service.description}
                    </p>

                    <div className="flex items-center justify-between pt-6 border-t border-border">
                      <div className="font-mono text-xs tracking-[0.12em] uppercase text-foreground font-medium">
                        {service.price}
                      </div>
                      <Button variant="ghost" className="font-default italic font-normal text-[0.95rem] p-0 h-auto group/btn" style={{
                    color: "var(--ring)"
                  }}>
                        
                        
                      </Button>
                    </div>

                    <div className="font-mono text-[0.65rem] tracking-[0.08em] uppercase text-muted-foreground mt-5">
                      {service.coords}
                    </div>

                    <div aria-hidden="true" className="absolute bottom-5 right-5 transition-transform duration-300 group-hover:rotate-[3deg]" style={{
                  transform: "rotate(-7deg)"
                }}>
                      <div className="relative size-16 flex items-center justify-center border-[1.5px] rounded-lg" style={{
                    borderColor: "var(--ring)",
                    color: "var(--ring)",
                    opacity: 0.85
                  }}>
                        <div className="absolute inset-1 border border-current rounded-lg opacity-50" />
                        <span className="font-mono text-[0.7rem] tracking-[0.15em] font-semibold">
                          {service.stamp}
                        </span>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}